import { createContext, useContext, useMemo } from 'react';
import { useCanAccess } from '@permissions/useFeatures';
import { areaReportTier, type AreaReportTier } from '../lib/locationStatusLabels';

/**
 * What the signed-in user may DO on the scheduler, one flag per write family.
 * The page and the task drawer each resolve it once (`useResolveSchedulerCapabilities`)
 * and hand it down by context — the surfaces are too deep to thread a prop
 * through, and a feature check in every leaf would scatter the decision.
 *
 * Convenience only: the server enforces each of these regardless. A flag that is off
 * means the control is NOT RENDERED (never disabled — role-permissions spec,
 * ruling 8); reading, opening, filtering, zooming and exporting stay live for
 * everyone, and a surface that is also the only place to read shows a read view
 * instead.
 */
export interface ISchedulerCapabilities {
  /** `schedule.edit` — Gantt edits, schedule create/delete/import, the task plan (profession, areas, crew). */
  canEdit: boolean;
  /** `schedule.pushToGenba` — the manual push to Genba. */
  canPush: boolean;
  /** `tasks.execute` — commit, status, progress, locations, stock claims, checklist ticks, comments. */
  canExecute: boolean;
  /** What a tap on an area may report (area review stage spec): `'execute'` (`tasks.execute`)
   *  done and the review, `'contribute'` (`tasks.contribute`) a submission for review, `null` nothing. */
  areaTier: AreaReportTier;
}

/** No default: a write control outside a resolving root must not fail open. */
export const SchedulerCapabilitiesContext = createContext<ISchedulerCapabilities | null>(null);

/** The capabilities the nearest scheduler root resolved. */
export function useSchedulerCapabilities(): ISchedulerCapabilities {
  const capabilities = useContext(SchedulerCapabilitiesContext);
  if (!capabilities) throw new Error('useSchedulerCapabilities must be used under a scheduler root (SchedulerPage or TaskPanel)');
  return capabilities;
}

/** Resolve the capabilities from the user's granted features — roots only. */
export function useResolveSchedulerCapabilities(): ISchedulerCapabilities {
  const canEdit = useCanAccess('schedule.edit');
  const canPush = useCanAccess('schedule.pushToGenba');
  const canExecute = useCanAccess('tasks.execute');
  const canContribute = useCanAccess('tasks.contribute');
  const areaTier = areaReportTier(canExecute, canContribute);
  return useMemo(
    () => ({ canEdit, canPush, canExecute, areaTier }),
    [canEdit, canPush, canExecute, areaTier],
  );
}
